import React, { useEffect, useMemo, useState } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { isOutingPast } from '../../../../utils/outingTiming';
import EditButton from '../../../ui/EditButton';

function formatOutingDate(value) {
  const date =
    value?.toDate?.() ?? (value instanceof Date ? value : value ? new Date(value) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return 'Date TBC';
  return date.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function sortOutings(left, right, direction = 'desc') {
  const leftDate = left?.date?.toDate?.()?.getTime?.() ?? 0;
  const rightDate = right?.date?.toDate?.()?.getTime?.() ?? 0;
  if (leftDate !== rightDate) {
    return direction === 'asc' ? leftDate - rightDate : rightDate - leftDate;
  }
  const leftUpdated = left?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  const rightUpdated = right?.updatedAt?.toDate?.()?.getTime?.() ?? 0;
  return rightUpdated - leftUpdated;
}

function statusLabel(status) {
  const normalized = String(status || 'draft').toLowerCase();
  if (normalized === 'submitted') return 'Submitted';
  return 'Draft';
}

function crewSummaryForOuting(crews = []) {
  return (Array.isArray(crews) ? crews : []).reduce(
    (acc, crew) => {
      const status = String(crew?.status || 'draft').toLowerCase();
      if (status === 'allocated') acc.allocated += 1;
      else if (status === 'unfilled') acc.unfilled += 1;
      else if (status === 'pending') acc.pending += 1;
      else acc.draft += 1;
      return acc;
    },
    { draft: 0, pending: 0, allocated: 0, unfilled: 0 }
  );
}

export default function OutingsCard({
  isAdmin,
  teamId,
  modules = {},
  onManage,
  title = 'Outings',
}) {
  const outingsEnabled = !!modules?.rowing?.outings?.enabled;
  const dependenciesMet = !!modules?.events?.enabled && !!modules?.rowing?.boatBooking?.enabled;
  const [outings, setOutings] = useState([]);
  const [dateFilter, setDateFilter] = useState('upcoming');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [crewSummaryByOuting, setCrewSummaryByOuting] = useState({});

  useEffect(() => {
    if (!isAdmin || !outingsEnabled || !dependenciesMet || !teamId) {
      setOutings([]);
      return undefined;
    }
    const unsubscribe = safeOnSnapshot(
      collection(db, 'teams', teamId, 'outings'),
      (snapshot) => {
        const rows = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort(sortOutings);
        setOutings(rows);
      },
      undefined,
      { label: 'rowing-outings', teamId }
    );
    return () => unsubscribe();
  }, [dependenciesMet, isAdmin, outingsEnabled, teamId]);

  useEffect(() => {
    if (!isAdmin || !outingsEnabled || !dependenciesMet || !teamId || outings.length === 0) {
      setCrewSummaryByOuting({});
      return undefined;
    }

    const summaries = new Map();
    const publish = () => {
      const next = {};
      outings.forEach((outing) => {
        next[outing.id] = summaries.get(outing.id) || { draft: 0, pending: 0, allocated: 0, unfilled: 0 };
      });
      setCrewSummaryByOuting(next);
    };

    const unsubscribes = outings.map((outing) =>
      safeOnSnapshot(
        collection(db, 'teams', teamId, 'outings', outing.id, 'crews'),
        (snapshot) => {
          summaries.set(
            outing.id,
            crewSummaryForOuting(snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })))
          );
          publish();
        },
        undefined,
        { label: 'rowing-outing-crews', teamId, outingId: outing.id }
      )
    );

    publish();
    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [dependenciesMet, isAdmin, outings, outingsEnabled, teamId]);

  const summary = useMemo(
    () =>
      outings.reduce(
        (acc, outing) => {
          const status = String(outing?.status || 'draft').toLowerCase();
          if (status === 'submitted') acc.submitted += 1;
          else acc.draft += 1;
          const crewSummary = crewSummaryByOuting[outing.id] || {};
          if (!isOutingPast(outing)) {
            acc.unfilled += Number(crewSummary.unfilled || 0);
          }
          return acc;
        },
        { draft: 0, submitted: 0, unfilled: 0 }
      ),
    [crewSummaryByOuting, outings]
  );

  const filteredOutings = useMemo(() => {
    const filtered = outings.filter((outing) => {
      if (dateFilter === 'all') return true;
      const outingIsPast = isOutingPast(outing);
      if (dateFilter === 'past') return outingIsPast;
      return !outingIsPast;
    }).filter((outing) => {
      if (!attentionOnly) return true;
      return !isOutingPast(outing) && Number(crewSummaryByOuting[outing.id]?.unfilled || 0) > 0;
    });
    return [...filtered].sort((left, right) =>
      sortOutings(left, right, dateFilter === 'past' ? 'desc' : 'asc')
    );
  }, [attentionOnly, crewSummaryByOuting, dateFilter, outings]);

  if (!isAdmin || !outingsEnabled) return null;

  return (
    <div className="team-card">
      <div className="card-header-with-icon">
        <span className="card-title">{title}</span>
        <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn--secondary btn--xs"
            onClick={() => onManage?.({ createNew: true })}
          >
            New outing
          </button>
          <EditButton
            section="outings"
            onEdit={() => onManage?.({ managePresets: true })}
          />
        </div>
      </div>

      <div className="card-body">
        {!dependenciesMet ? (
          <p className="text-muted">
            Enable both Events and Boat Booking before using Outings.
          </p>
        ) : (
          <>
            <div className="availability-status-list">
              <div className="availability-status-item">
                <div className="availability-status-label">
                  <span className="availability-status-icon">🧭</span>
                  <span>Drafts</span>
                </div>
                <span className="availability-pill unavailable">{summary.draft}</span>
              </div>

              <div className="availability-status-item">
                <div className="availability-status-label">
                  <span className="availability-status-icon">🚣</span>
                  <span>Submitted</span>
                </div>
                <span className="availability-pill available">{summary.submitted}</span>
              </div>

              <div className="availability-status-item">
                <div className="availability-status-label">
                  <span className="availability-status-icon">⚠️</span>
                  <span>No boat</span>
                </div>
                <span className="availability-pill unavailable">{summary.unfilled}</span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              <button
                type="button"
                className={dateFilter === 'upcoming' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
                onClick={() => setDateFilter('upcoming')}
              >
                Upcoming
              </button>
              <button
                type="button"
                className={dateFilter === 'all' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
                onClick={() => setDateFilter('all')}
              >
                All
              </button>
              <button
                type="button"
                className={dateFilter === 'past' ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
                onClick={() => setDateFilter('past')}
              >
                Past
              </button>
              <button
                type="button"
                className={attentionOnly ? 'btn btn--primary btn--xs' : 'btn btn--secondary btn--xs'}
                onClick={() => setAttentionOnly((prev) => !prev)}
              >
                Needs attention
              </button>
            </div>

            {filteredOutings.length === 0 ? (
              <p className="text-muted" style={{ marginTop: 12 }}>
                No outings in this date range.
              </p>
            ) : (
              <div style={{ marginTop: 12, display: 'grid', gap: 8, maxHeight: '20rem', overflowY: 'auto', paddingRight: 4 }}>
                {filteredOutings.map((outing) => (
                  (() => {
                    const crewSummary = crewSummaryByOuting[outing.id] || { draft: 0, pending: 0, allocated: 0, unfilled: 0 };
                    const needsAttention = !isOutingPast(outing) && Number(crewSummary.unfilled || 0) > 0;
                    const pendingCount = Number(crewSummary.pending || 0);
                    const allocatedCount = Number(crewSummary.allocated || 0);
                    return (
                      <button
                        key={outing.id}
                        type="button"
                        className="btn btn--secondary"
                        style={{
                          justifyContent: 'space-between',
                          display: 'flex',
                          alignItems: 'center',
                          textAlign: 'left',
                          gap: 12,
                          borderColor: needsAttention ? '#dc2626' : undefined,
                        }}
                        onClick={() => onManage?.({ outingId: outing.id })}
                      >
                        <span>
                          <strong>{outing.name || outing.linkedEventName || 'Outing'}</strong>
                          <span className="text-muted" style={{ display: 'block', marginTop: 2 }}>
                            {formatOutingDate(outing.date)} {outing.fromTime ? `• ${outing.fromTime}` : ''}
                          </span>
                          <span className="text-muted" style={{ display: 'block', marginTop: 4, fontSize: '0.88rem' }}>
                            {needsAttention
                              ? `${crewSummary.unfilled} with no boat`
                              : pendingCount > 0
                                ? `${pendingCount} pending allocation`
                                : allocatedCount > 0
                                  ? `${allocatedCount} allocated`
                                  : statusLabel(outing.status)}
                          </span>
                        </span>
                        <span className={`availability-pill ${needsAttention ? 'unavailable' : 'available'}`}>
                          {needsAttention ? 'No boat' : statusLabel(outing.status)}
                        </span>
                      </button>
                    );
                  })()
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
